// ** React Imports
import { forwardRef, useState } from 'react'

// ** MUI Imports
import Grid from '@mui/material/Grid'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fade,
  IconButton,
  Link,
  Typography,
  Paper
} from '@mui/material'

// ** Styled Component Import
import { styled } from '@mui/material/styles'
import { Icon } from '@iconify/react'
import CustomHeader from 'src/@core/components/Header'

// ** Data Import
import { contributorsList } from 'src/data/general/contributors'

// ** Styled Components
const PurpleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText('#0278AE'),
  marginTop: '8px',
  background: 'linear-gradient(to right, #265faf, #1c5db9, #10499b, #043987)!important;',
  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    background: 'linear-gradient(to right, #8852ff, #632ecb, #4e19c1, #381683)!important',
    boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.15)'
  }
}))

// ** URLs for External Links
const urls = {
  dataGathering: 'https://tinyurl.com/waotoolsdata',
  paypal: 'https://www.paypal.com/paypalme/oNaare'
}

// ** Support Options
const supportOptions = [
  {
    text: 'Support The Project',
    href: 'https://www.paypal.com/paypalme/oNaare',
    linkProps: { target: '_blank', rel: 'noopener' },
    icon: <Icon icon='logos:paypal' fontSize={40} />
  },
  {
    text: 'Buy Me a Coffee',
    href: 'https://www.buymeacoffee.com/onadev',
    linkProps: { target: '_blank', rel: 'noopener' },
    icon: <Icon icon='simple-icons:buymeacoffee' fontSize={40} />
  },
  {
    text: 'Support The Project',
    href: 'https://www.patreon.com/oNare',
    linkProps: { target: '_blank', rel: 'noopener' },
    icon: <Icon icon='logos:patreon' fontSize={40} />
  }
]

// ** Transition for Dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} />
})

// ** Icons for Contributors
const contributorIcons = {
  supporters: <Icon icon='solar:cup-star-bold' fontSize={30} style={{ color: 'currentColor' }} />,
  developers: <Icon icon='iconoir:developer' fontSize={30} style={{ color: 'currentColor' }} />,
  contributors: <Icon icon='la:hands-helping' fontSize={30} style={{ color: 'currentColor' }} />
}

// ** ContributorsDetails Component with Improved VIP Tag Style
const ContributorsDetails = ({ title, data, icon, borderColor }) => {
  return (
    <Box
      sx={{
        padding: '1.5rem',
        marginBottom: '2rem',
        borderRadius: '12px',
        // background.paper: con blanco fijo, en modo oscuro el texto claro quedaba sobre blanco
        backgroundColor: 'background.paper',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        borderLeft: `6px solid ${borderColor}` // Dynamic border color
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        {icon}
        <Typography variant='h5' component='p' color='primary' sx={{ marginLeft: '12px', fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ marginBottom: '1rem' }} />
      <Grid container spacing={3}>
        {data.map(ct => (
          <Grid size={{ xs: 12 }}key={ct.name}>
            <Typography variant='body2' sx={{ fontWeight: 'bold', display: 'inline' }}>
              {ct.name}
            </Typography>
            <Typography variant='body2' sx={{ marginLeft: '8px', color: 'text.secondary', display: 'inline' }}>
              {ct.realm} {ct.desc}
            </Typography>
            {['VIP supporter', 'VIP contributor'].includes(ct.type) && (
              <Box
                sx={{
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: '#FFD700', // Gold background
                  color: '#000', // Black text
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                  textTransform: 'uppercase'
                }}
              >
                VIP
              </Box>
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

// ** Main Contributors Component
const Contributors = () => {
  const [openCoffee, setOpenCoffee] = useState(false)

  return (
    <>
      {/* Welcome Section with Blue Border */}
      <Card
        sx={{
          padding: '1rem 2rem',
          marginBottom: '2rem',
          borderRadius: '12px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          borderTop: '6px solid',
          borderTopColor: 'primary.main'
        }}
      >
        <CustomHeader icon='contributors' title='CONTRIBUTORS' />
        <Divider sx={{ marginY: '0.5rem' }} />
        <Typography variant='h6' component='h2' color='primary' align='center' sx={{ marginBottom: '1rem', fontWeight: 'bold' }}>
          INFORMATION IS POWER!
        </Typography>
        <Typography variant='body1' align='center' sx={{ marginBottom: '1.5rem', color: 'text.secondary' }}>
          This is a compilation of a hundred pictures, spreadsheets, and information gathered through the years from
          people who shared it. Make good use of all these tools and information. If you want to continue supporting
          this website and project, go and buy us some coffee {<Icon icon='iconoir:coffee-cup' />} here:
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <PurpleButton
            variant='contained'
            startIcon={<Icon icon='game-icons:coffee-cup' />}
            onClick={() => setOpenCoffee(true)}
          >
            BUY ME SOME COFFEES
          </PurpleButton>
        </Box>
        <Typography variant='body1' align='center' sx={{ color: 'text.secondary' }}>
          If you want to contribute, give any ideas or report a bug, please leave me a comment:
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          {/*
            El boton ES el enlace. Antes envolvia un <a> adentro, y un control
            interactivo dentro de otro confunde a los lectores de pantalla y al
            teclado (audit nested-interactive).
          */}
          <PurpleButton
            variant='contained'
            startIcon={<Icon icon='mdi:google-spreadsheet' />}
            component='a'
            href={urls.dataGathering}
            target='_blank'
            rel='noopener'
          >
            CONTRIBUTE
          </PurpleButton>
        </Box>
      </Card>

      {/* Developers Section (No Border) */}
      <ContributorsDetails
        title='Developers'
        data={contributorsList.filter(ct => ['creator', 'developer'].includes(ct.type))}
        icon={contributorIcons.developers}
        borderColor='transparent' // No border
      />

      {/* Supporters Section with Orange Border */}
      <ContributorsDetails
        title='Supporters'
        data={contributorsList.filter(ct => ['supporter', 'VIP supporter'].includes(ct.type))}
        icon={contributorIcons.supporters}
        borderColor='#FF9800' // Orange border
      />

      {/* Contributors Section (No Border) */}
      <ContributorsDetails
        title='Contributors'
        data={contributorsList.filter(ct => ['contributor', 'VIP contributor'].includes(ct.type))}
        icon={contributorIcons.contributors}
        borderColor='transparent' // No border
      />

      {/* Dialog | Support */}
      <Dialog
        open={openCoffee}
        maxWidth='md'
        scroll='body'
        onClose={() => setOpenCoffee(false)}
        TransitionComponent={Transition}
      >
        <DialogContent sx={{ textAlign: 'center', padding: '2rem' }}>
          <IconButton
            size='small'
            aria-label='Close'
            onClick={() => setOpenCoffee(false)}
            sx={{ position: 'absolute', right: '1rem', top: '1rem' }}
          >
            <Icon icon='mdi:close' />
          </IconButton>
          <Typography variant='h5' component='p' sx={{ marginBottom: '1rem', fontWeight: 'bold' }}>
            Buy me some coffees!
          </Typography>
          <Typography variant='body2' sx={{ marginBottom: '2rem', color: 'text.secondary' }}>
            The work I do coding this website is for your use and everyone playing War and Order. Your support is vital
            to continue delivering these amazing updates!
          </Typography>
          <Divider sx={{ marginBottom: '2rem' }} />
          {supportOptions.map((option, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}
            >
              {option.icon}
              <Typography variant='body1' sx={{ marginLeft: '1rem' }}>
                <Link target='_blank' rel='noopener' component='a' href={option.href}>
                  {option.text}
                </Link>
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button color='primary' onClick={() => setOpenCoffee(false)}>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

Contributors.noLoadingGate = true

export default Contributors
